import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import "./SubscriptionSettings.css";
import { checkIcon } from "../../../../icons/assets";
import {
  cancelSubscription,
  getStatus as getSubscriptionStatus,
} from "../../../../services/api/app/subscriptions";
import { mapProductPlanName } from "../../../../utils/planNames";
import logger from "../../../../utils/logger";

const FREE_BENEFITS = {
  has_subscription: false,
  plan_name: "Pay as you go",
  monthly_credits: 0,
  benefits: [
    "Pay per use",
    "No commitment",
    "50 GB storage",
    "Credits never expire",
    "Commercial license",
  ],
};

/** Show product plan names (Lite / Pro / Max / Pay as you go). */
const formatPlanName = (name, credits = null, hasSubscription = false) => {
  if (!hasSubscription) {
    const raw = String(name || "").trim();
    if (!raw) return "Pay as you go";
    if (/^free$/i.test(raw)) return "Free";
    if (/pay/i.test(raw)) return "Pay as you go";
    return raw;
  }
  return mapProductPlanName(name, credits);
};

const formatPrice = (price, currency = "USD") => {
  if (price == null || Number.isNaN(Number(price))) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(Number(price));
  } catch {
    return `$${Number(price).toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const diff = target.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

const cycleLabel = (benefits) => {
  if (!benefits?.has_subscription) return "Flexible";
  if (!benefits?.start_date || !benefits?.end_date) return "Monthly";
  const days =
    (new Date(benefits.end_date) - new Date(benefits.start_date)) / 86400000;
  return days > 180 ? "Yearly" : "Monthly";
};

const SubscriptionSettings = ({ onChangePlan, onSubscriptionChanged }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [benefits, setBenefits] = useState(FREE_BENEFITS);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    setConfirmCancel(false);

    try {
      const token = await getToken();
      if (!token) {
        setBenefits(FREE_BENEFITS);
        setLoading(false);
        return;
      }

      const response = await getSubscriptionStatus(token);
      if (response?.success && response.benefits) {
        const next = response.benefits;
        const hasSub = Boolean(next.has_subscription);
        setBenefits({
          ...next,
          plan_name: formatPlanName(
            next.plan_name || FREE_BENEFITS.plan_name,
            next.monthly_credits,
            hasSub
          ),
        });
      } else {
        setBenefits(FREE_BENEFITS);
      }
    } catch (err) {
      logger.error("Failed to load subscription status", err);
      setError("Couldn’t check your plan right now.");
      setBenefits(FREE_BENEFITS);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const hasSubscription = Boolean(benefits?.has_subscription);
  const featureList = useMemo(() => {
    if (Array.isArray(benefits?.benefits) && benefits.benefits.length) {
      return benefits.benefits;
    }
    if (hasSubscription) {
      return [`${benefits?.monthly_credits ?? 0} Credits per month`];
    }
    return FREE_BENEFITS.benefits;
  }, [benefits, hasSubscription]);

  const priceText = formatPrice(benefits?.price, benefits?.currency);
  const renewDate = formatDate(benefits?.end_date);
  const billingCycle = cycleLabel(benefits);
  const renewByDate = formatDate(benefits?.renew_by_date);
  const renewByDays = daysUntil(benefits?.renew_by_date);
  const accessPhase = benefits?.access_phase || (hasSubscription ? "active" : "free");
  const retainedCredits = Number(
    benefits?.locked_subscription_credits ??
      benefits?.retained_subscription_credits ??
      0
  );

  const renewalText = hasSubscription
    ? renewDate
      ? benefits?.auto_renewal === false
        ? `Access until ${renewDate}`
        : `Renews on ${renewDate}`
      : "Active subscription"
    : accessPhase === "grace" && renewByDate
      ? `Renew by ${renewByDate} to keep ${retainedCredits} credits`
      : accessPhase === "soft_locked" && renewByDate
        ? `Renew by ${renewByDate} to unlock ${retainedCredits} credits`
        : benefits?.plan_name === "Free"
          ? "No credits available right now"
          : "No renewal - pay only when you buy credits";

  const statusMessageAfterCancel =
    accessPhase === "grace"
      ? `Plan cancelled. Renew within ${renewByDays ?? 15} days to keep your remaining credits.`
      : "Plan cancelled. You’ll keep access until the end of this billing period.";

  const upsellTitle =
    accessPhase === "soft_locked"
      ? "Renew to unlock your credits"
      : accessPhase === "grace"
        ? "Keep your remaining credits"
        : "Upgrade for monthly credits";

  const upsellCopy =
    accessPhase === "soft_locked"
      ? `Your ${retainedCredits} leftover ${benefits?.previous_plan_name || ""}`.trim() +
        ` credits are soft-locked. Renew${renewByDate ? ` by ${renewByDate}` : ""} to unlock them.`
      : accessPhase === "grace"
        ? `You can still use your leftover credits for now. Renew${renewByDate ? ` by ${renewByDate}` : ""} to keep ${retainedCredits} credits from your ${benefits?.previous_plan_name || "previous"} plan.`
        : "Get recurring credits, more storage, and plan perks that renew automatically - or stay on Pay as you go and buy only what you need.";

  const handleCancel = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      setStatusMessage("");
      setError("");
      return;
    }

    setCancelling(true);
    setError("");
    setStatusMessage("");

    try {
      const token = await getToken();
      if (!token) throw new Error("Not signed in");

      const result = await cancelSubscription(token);
      if (!result?.success) {
        throw new Error(result?.message || "Cancel failed");
      }

      setStatusMessage(statusMessageAfterCancel);
      setConfirmCancel(false);
      await loadStatus();
      onSubscriptionChanged?.();
    } catch (err) {
      logger.error("Failed to cancel subscription", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn’t cancel your plan. Please try again."
      );
      setConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  const headerStatus = loading
    ? "Checking your plan…"
    : cancelling
      ? "Cancelling your plan…"
      : error
        ? error
        : statusMessage || "";

  const headerStatusTone = error
    ? "error"
    : statusMessage && !loading && !cancelling
      ? "success"
      : "muted";

  return (
    <div className="subscription-settings-container">
      <div className="subscription-settings-heading">
        <h2>Subscription</h2>
        <p
          className={`subscription-header-status subscription-header-status--${headerStatusTone}`}
          role="status"
          aria-live="polite"
        >
          {headerStatus || "\u00A0"}
        </p>
      </div>

      <section
        className={`subscription-plan-card${loading ? " is-loading" : ""}${
          hasSubscription ? " is-paid" : " is-free"
        }`}
        aria-busy={loading}
      >
        <div className="subscription-plan-card__top">
          <div className="subscription-plan-card__meta">
            <p className="subscription-plan-card__eyebrow">
              {hasSubscription ? "Current plan" : "You’re on"}
            </p>
            <div className="subscription-plan-card__title-row">
              <h3 className="subscription-plan-card__name">
                {formatPlanName(
                  benefits?.plan_name,
                  benefits?.monthly_credits,
                  hasSubscription
                )}
              </h3>
              {renewalText ? (
                <p className="subscription-plan-card__renewal">{renewalText}</p>
              ) : null}
            </div>
          </div>
          <span
            className={`subscription-plan-badge subscription-plan-badge--${
              hasSubscription ? "pro" : "free"
            }`}
          >
            <span className="subscription-plan-badge__dot" aria-hidden="true" />
            {billingCycle}
          </span>
        </div>

        <div className="subscription-plan-price-row">
          {hasSubscription && priceText ? (
            <p className="subscription-plan-price">{priceText}</p>
          ) : (
            <span className="subscription-usage-badge">
              <span className="subscription-usage-badge__dot" aria-hidden="true" />
              Usage based
            </span>
          )}
          <p className="subscription-plan-price-note">
            {hasSubscription
              ? `Billed ${billingCycle.toLowerCase()}`
              : "No monthly commitment"}
          </p>
        </div>

        <div className="subscription-plan-footer">
          <ul className="subscription-feature-list">
            {featureList.map((feature) => (
              <li className="subscription-feature" key={feature}>
                <img src={checkIcon} alt="" aria-hidden="true" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <div className="subscription-plan-actions">
            <button
              type="button"
              className="subscription-primary-btn"
              onClick={() => onChangePlan?.()}
              disabled={loading || cancelling}
            >
              {hasSubscription ? "Change plan" : "Upgrade"}
            </button>
            {!hasSubscription && (
              <button
                type="button"
                className="subscription-secondary-btn"
                onClick={() => onChangePlan?.()}
                disabled={loading || cancelling}
              >
                Compare plans
              </button>
            )}
          </div>
        </div>
      </section>

      {hasSubscription ? (
        <section className="subscription-danger-card">
          <h3 className="subscription-danger-card__title">Cancel subscription</h3>
          <p className="subscription-danger-card__copy">
            {confirmCancel
              ? "Confirm to cancel. You’ll keep paid features until the end of this billing period, then get 15 days to renew and keep leftover subscription credits."
              : "Cancel anytime. You’ll keep access until the current period ends, then have 15 days to renew before leftover subscription credits are soft-locked."}
          </p>
          <div className="subscription-danger-card__actions">
            {confirmCancel && (
              <button
                type="button"
                className="subscription-secondary-btn"
                onClick={() => setConfirmCancel(false)}
                disabled={cancelling}
              >
                Keep plan
              </button>
            )}
            <button
              type="button"
              className="subscription-danger-btn"
              onClick={handleCancel}
              disabled={cancelling || loading}
            >
              {cancelling
                ? "Cancelling…"
                : confirmCancel
                  ? "Confirm cancel"
                  : "Cancel subscription"}
            </button>
          </div>
        </section>
      ) : (
        <section className="subscription-upsell-card">
          <h3 className="subscription-upsell-card__title">{upsellTitle}</h3>
          <p className="subscription-upsell-card__copy">
            {upsellCopy}
          </p>
          <button
            type="button"
            className="subscription-primary-btn"
            onClick={() => onChangePlan?.()}
            disabled={loading}
          >
            {accessPhase === "soft_locked" || accessPhase === "grace"
              ? "Renew plan"
              : "Upgrade now"}
          </button>
        </section>
      )}
    </div>
  );
};

export default SubscriptionSettings;
